import React, { Fragment, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme, Box, Typography } from '@mui/material';
import { getRandomColor } from 'state/helpers';


const BarChart = ({ 
  data = [], 
  title = "",
  bottomLegend, 
  leftLegend, 
  isShowAvgLine, 
  isHorizontalBottomLegend, 
  digit = 2,
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
  itemWidthOffSet = 0,
  translateXOffSet = 0,
  translateYOffSet = 0,
  itemsSpacingOffSet = 0,
}) => {
  const theme = useTheme();
    
  const colorMap = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }

    return data.reduce((acc, cur) => {
      acc[Object.values(cur)[0]] = getRandomColor(); 
      return acc;
    }, {});
  }, [data]);    
  
  if (!data || data.length === 0 || !Array.isArray(data)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }
  
  const formattedData = data.map(e => {
    // return {
    //   ...e,
    //   y: e.y ? Number(e.y.toFixed(digit)) : 0,
    // }
    return Object.fromEntries(
      Object.entries(e).map(([key, value]) => [
        key, 
        typeof value === "number" ? Number(value.toFixed(digit)) : value
      ])
    );
  });



  const total = data && data.reduce((acc, cur) => acc + cur.y, 0);
  const average = data.length !== 0 && total / data.length;

  /* Average Line */
  const CustomLine = ({ yScale, innerWidth }) => {
    return (
      <Fragment>        
        <line
          x1={0}
          x2={innerWidth}
          y1={yScale(average)}
          y2={yScale(average)}
          stroke='hsl(200, 70%, 50%)'
          strokeDasharray="8 6"
          strokeWidth={1}
        />
        
        <text
          x={innerWidth - 35}  
          y={yScale(average) - 5}  
          fill={theme.palette.text.primary}
          fontSize={12}
        >
          {`${average.toFixed(digit)}`} 
        </text>
      </Fragment>
    );
  };


  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      gap={1} 
      sx={{ height: '100%', width: '100%' }}
    >
      {title && 
        <Typography 
          variant="h6" 
          align="center"
        >
          {title}
        </Typography>
      }

      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <ResponsiveBar
          data={formattedData}        
          indexBy={`${Object.keys(data[0])[0]}`}
          keys={Object.keys(data[0]).slice(1)}
          margin={{ 
            top: 25 + topOffSet,
            right: 20, 
            bottom: 155 + bottomOffSet, 
            left: 80 
          }}
          padding={0.3}
          groupMode="grouped"
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={Object.keys(data[0]).length > 2 ? { scheme: 'set2' } : ({ indexValue }) => colorMap[indexValue]}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "#38bcb2",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "#eed312",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          borderColor={{ from: 'color', modifiers: [['darker', 1.5]] }}        
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,          
            tickPadding: 45 + bottomLegendOffSet,
            tickRotation: isHorizontalBottomLegend ? 0 : 270,
            legend: bottomLegend,
            legendPosition: 'middle',
            legendOffset: 75 + bottomLegendOffSet,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: leftLegend || 'Value',
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          enableTotals={true}
          totalsOffset={8}
          totalsTextColor={theme.palette.text.primary}
          enableLabel={true} 
          labelTextColor={theme.palette.text.primary}
          labelSkipWidth={4}
          labelSkipHeight={4}
          labelPosition="end"
          labelOffset={8}
          animate={true}
          motionStiffness={90}
          motionDamping={15}  
          layers={['grid', 'axes', 'bars', 'markers', 'legends', isShowAvgLine && (CustomLine)].filter(Boolean)}
          legends={
            !Object.keys(data[0]).includes('y') // Conditionally show or hide the legend based on 'y'
              ? [{
                  dataFrom: 'keys', 
                  anchor: 'top-right', 
                  direction: 'row', 
                  justify: false,
                  translateX: 0 + translateXOffSet, 
                  translateY: -25 + translateYOffSet,
                  itemWidth: 60 + itemWidthOffSet,
                  itemHeight: 20,
                  itemsSpacing: 2 + itemsSpacingOffSet, 
                  symbolSize: 12, 
                  symbolShape: 'circle', 
                  itemDirection: 'left-to-right',
                  itemOpacity: 1,
                }]
              : [] // Hide legend if 'y' is a key
          }        
          theme={{
            axis: {
              domain: {
                line: {
                  stroke: theme.palette.neutral.dark,
                },
              },
              legend: {
                text: {
                  fill: theme.palette.text.primary,
                },
              },
              ticks: {
                line: {
                  stroke: theme.palette.neutral.dark,
                  strokeWidth: 1,
                },
                text: {
                  fill: theme.palette.text.primary,
                },
              },
            },          
            legends: {
              text: {
                fill: theme.palette.text.primary,
              },
            }, 
            grid: {
              line: {
                stroke: theme.palette.neutral.dark,
                strokeWidth: 0.2,
                strokeDasharray: '2 2'
              }
            },
            tooltip: {
              container: {
                background: theme.palette.neutral.light,
                color: theme.palette.text.primary,
                borderRadius: '4px',
                padding: '8px',
              },
            },
            labels: {
              text: {
                fill: theme.palette.text.primary,
                fontSize: '12px' 
              },
            },
            dots: {
              text: {
                fill: theme.palette.text.primary,
              },
            },
            totals: {
              text: {
                fill: theme.palette.text.primary, 
                fontSize: '12px',
              },
            },
          }}      
        />
      </Box>
    </Box>
  );
};

export default BarChart;
